import { countScheduledDevicesForDay } from 'domains/shifts/components/components/performance-overview/utils/countScheduledDevices'
import { type ShopperShiftsPerformanceMetrics } from 'domains/shifts/components/components/performance-overview/utils/types'
import { getStaffingLevelsForShiftType } from 'domains/shifts/components/components/staffing-levels/utils/getStaffingLevelsForShiftType'
import { type StaffingLevel } from 'domains/shifts/components/components/staffing-levels/utils/types'
import { type ShiftType, type ShopperWithShifts } from 'domains/shifts/components/types'
import { calculateMatchRate } from 'domains/shifts/components/components/performance-overview/utils/calculateMatchRate'

export const computeDailyMetrics: (
  shiftType: ShiftType,
  staffingLevels: StaffingLevel[],
  shoppers: ShopperWithShifts[],
  shouldCalculateMatchRate: boolean
) => ShopperShiftsPerformanceMetrics = (
  shiftType,
  staffingLevels,
  shoppers,
  shouldCalculateMatchRate
) => {
  const staffingLevelsForShiftType = getStaffingLevelsForShiftType(staffingLevels, shiftType)
  const metrics: ShopperShiftsPerformanceMetrics = {
    hoursRecommended: 0,
    hoursScheduled: 0,
    hoursUnderstaffed: 0,
    hoursOverstaffed: 0,
    scheduleGaps: 0,
    matchedHours: 0,
    scheduledDevices: countScheduledDevicesForDay(shoppers, shiftType),
  }

  staffingLevelsForShiftType.forEach(({ scheduled, required }) => {
    metrics.hoursScheduled += scheduled
    metrics.matchedHours += Math.min(scheduled, required ?? 0)

    if (required !== null) {
      metrics.hoursRecommended += required
    }

    if (scheduled < required) {
      metrics.hoursUnderstaffed += required - scheduled
    }

    if (scheduled > required) {
      metrics.hoursOverstaffed += scheduled - required
    }

    if (scheduled === 0 && required > 0) {
      metrics.scheduleGaps += 1
    }
  })

  if (metrics.hoursRecommended > 0) {
    if (shouldCalculateMatchRate) {
      metrics.matchRate = calculateMatchRate({
        hoursRecommended: metrics.hoursRecommended,
        matchedHours: metrics.matchedHours,
      })
    }

    return metrics
  } else {
    // if there were no recommendations, skip metrics based on recommendations
    return {
      scheduledDevices: metrics.scheduledDevices,
      hoursScheduled: metrics.hoursScheduled,
      hoursRecommended: undefined,
      hoursUnderstaffed: undefined,
      hoursOverstaffed: undefined,
      scheduleGaps: undefined,
      matchRate: shouldCalculateMatchRate ? null : undefined,
    }
  }
}
